import { CloneMetricSparkCell } from '@/features/tests/components/testsColumns.cloneCells';
import {
  mapChipKeyToStatusTone,
  resolveCloneMetricAccentColor,
} from '@/features/tests/components/testsColumns.cloneShared';
import { formatDuration, formatFrequency, formatPercent } from '@/ui';
import { getThresholdChipKey } from '@/ui/composites/table/tableUtils';
import { type GridColDef, type GridRenderCellParams, type Theme } from '@/ui/primitives';
import { resolveLatestSeriesIndex, resolveSeriesValue } from './cloneSparklineUtils';
import type { MetricThresholdGroup } from '@flowqa/shared';

type CloneMetricSeries = {
  dates: Date[];
  passRate: number[];
  frequencyPerDay: number[];
  averageDurationMs: number[];
};

type CloneMetricRow = {
  id: string;
  metrics?: {
    passRate?: number | null;
    frequencyPerDay?: number | null;
    averageDurationMs?: number | null;
    delta?: {
      passRate?: number | null;
      frequencyPerDay?: number | null;
      averageDurationMs?: number | null;
    } | null;
  } | null;
  latestExecution?: {
    timestamp?: Date | string | null;
  } | null;
};

const resolveSeriesDelta = (values: number[] | undefined, index: number | null): number | null => {
  if (!values || index === null || index <= 0 || index >= values.length) return null;
  return values[index] - values[index - 1];
};

export type BuildCloneSparklineColumnsArgs<Row extends CloneMetricRow> = {
  passRateAsSparklineCell: boolean;
  frequencyAsSparklineCell: boolean;
  avgDurationAsSparklineCell: boolean;
  showDeltas: boolean;
  resolvedThresholds: (row: Row) => MetricThresholdGroup;
  metricSeriesById?: Map<string, CloneMetricSeries>;
  columnFlex: {
    passRate: number;
    frequency: number;
    avgDuration: number;
  };
  cellClassNames: {
    passRate: string;
    frequency: string;
    avgDuration: string;
  };
};

export type CloneSparklineColumns<Row extends CloneMetricRow> = {
  passRateSparklineColumn: GridColDef<Row> | null;
  frequencySparklineColumn: GridColDef<Row> | null;
  avgDurationSparklineColumn: GridColDef<Row> | null;
};

export const buildCloneSparklineColumns = <Row extends CloneMetricRow>({
  passRateAsSparklineCell,
  frequencyAsSparklineCell,
  avgDurationAsSparklineCell,
  showDeltas,
  resolvedThresholds,
  metricSeriesById,
  columnFlex,
  cellClassNames,
}: BuildCloneSparklineColumnsArgs<Row>): CloneSparklineColumns<Row> => {
  const passRateSparklineColumn: GridColDef<Row> | null = passRateAsSparklineCell
    ? {
        field: 'metrics.passRate',
        headerName: 'Pass Rate',
        flex: columnFlex.passRate,
        align: 'left',
        headerAlign: 'left',
        cellClassName: cellClassNames.passRate,
        filterable: false,
        sortable: true,
        valueGetter: (_value, row) => row.metrics?.passRate ?? null,
        renderCell: (params: GridRenderCellParams<Row>) => {
          const passRate = params.row.metrics?.passRate ?? null;
          const passRateDelta = showDeltas ? (params.row.metrics?.delta?.passRate ?? null) : null;
          const metricSeries = metricSeriesById?.get(params.row.id);
          const seriesIndex = resolveLatestSeriesIndex(metricSeries);
          const seriesPassRate = resolveSeriesValue(metricSeries?.passRate, seriesIndex);
          const seriesDelta = showDeltas
            ? resolveSeriesDelta(metricSeries?.passRate, seriesIndex)
            : null;
          const hasSeriesExecutions = seriesIndex !== null;
          const resolvedPassRate =
            metricSeries && !hasSeriesExecutions ? null : (passRate ?? seriesPassRate);
          const resolvedPassRateDelta =
            resolvedPassRate === null ? null : (passRateDelta ?? seriesDelta);
          const passRateLabel =
            resolvedPassRate === null ? 'No data' : `${formatPercent(resolvedPassRate)}%`;
          const deltaLabel =
            resolvedPassRateDelta === null
              ? null
              : resolvedPassRateDelta === 0
                ? 'No change'
                : `${resolvedPassRateDelta > 0 ? '+' : ''}${formatPercent(resolvedPassRateDelta)}%`;
          const thresholdGroup = resolvedThresholds(params.row);
          const accent = getThresholdChipKey(resolvedPassRate, thresholdGroup.passRate);
          const tone = mapChipKeyToStatusTone(accent);
          const valueColor =
            resolvedPassRate === null
              ? 'text.secondary'
              : (muiTheme: Theme): string => resolveCloneMetricAccentColor(muiTheme, tone);
          const deltaIsGood = resolvedPassRateDelta === null ? null : resolvedPassRateDelta > 0;
          const deltaColor = 'text.secondary';
          const sparklineData = metricSeries?.passRate ?? [];
          const sparklineDates = metricSeries?.dates ?? [];
          const sparkStroke = valueColor;
          const resolveTooltipSwatchColor = (
            muiTheme: Theme,
            sparkValue: number | null,
          ): string => {
            if (resolvedPassRate === null) {
              return resolveCloneMetricAccentColor(muiTheme, 'neutral');
            }
            const sparkAccent = getThresholdChipKey(sparkValue, thresholdGroup.passRate);
            const sparkTone = mapChipKeyToStatusTone(sparkAccent);
            return resolveCloneMetricAccentColor(muiTheme, sparkTone);
          };
          return (
            <CloneMetricSparkCell
              valueLabel={passRateLabel}
              hasValue={resolvedPassRate !== null}
              deltaLabel={deltaLabel}
              deltaIsGood={deltaIsGood}
              deltaColor={deltaColor}
              yAxisMin={0}
              yAxisMax={100}
              sparklineData={sparklineData}
              sparklineDates={sparklineDates}
              sparkStroke={sparkStroke}
              sparklineValueFormatter={(sparkValue) =>
                sparkValue === null ? '-' : `${formatPercent(sparkValue)}%`
              }
              resolveTooltipSwatchColor={resolveTooltipSwatchColor}
            />
          );
        },
      }
    : null;

  const frequencySparklineColumn: GridColDef<Row> | null = frequencyAsSparklineCell
    ? {
        field: 'metrics.frequencyPerDay',
        headerName: 'Frequency',
        flex: columnFlex.frequency,
        align: 'left',
        headerAlign: 'left',
        cellClassName: cellClassNames.frequency,
        filterable: false,
        sortable: true,
        valueGetter: (_value, row) => row.metrics?.frequencyPerDay ?? null,
        renderCell: (params: GridRenderCellParams<Row>) => {
          const value = params.row.metrics?.frequencyPerDay ?? null;
          const delta = showDeltas ? (params.row.metrics?.delta?.frequencyPerDay ?? null) : null;
          const metricSeries = metricSeriesById?.get(params.row.id);
          const seriesIndex = resolveLatestSeriesIndex(metricSeries);
          const seriesValue = resolveSeriesValue(metricSeries?.frequencyPerDay, seriesIndex);
          const seriesDelta = showDeltas
            ? resolveSeriesDelta(metricSeries?.frequencyPerDay, seriesIndex)
            : null;
          const hasSeriesExecutions = seriesIndex !== null;
          const resolvedValue =
            metricSeries && !hasSeriesExecutions
              ? null
              : value !== null && value > 0
                ? value
                : (seriesValue ?? value);
          const resolvedDelta = showDeltas
            ? resolvedValue === null
              ? null
              : (delta ?? seriesDelta)
            : null;
          const valueLabel =
            resolvedValue === null ? formatFrequency(0) : formatFrequency(resolvedValue);
          const deltaLabel =
            resolvedDelta === null
              ? null
              : resolvedDelta === 0
                ? 'No change'
                : `${resolvedDelta > 0 ? '+' : resolvedDelta < 0 ? '-' : ''}${formatFrequency(
                    Math.abs(resolvedDelta),
                  )}`;
          const thresholdGroup = resolvedThresholds(params.row);
          const accent = getThresholdChipKey(resolvedValue, thresholdGroup.frequency);
          const tone = mapChipKeyToStatusTone(accent);
          const valueColor =
            resolvedValue === null
              ? 'text.secondary'
              : (muiTheme: Theme): string => resolveCloneMetricAccentColor(muiTheme, tone);
          const deltaIsGood = resolvedDelta === null ? null : resolvedDelta > 0;
          const deltaColor = 'text.secondary';
          const sparklineData = metricSeries?.frequencyPerDay ?? [];
          const sparklineDates = metricSeries?.dates ?? [];
          const frequencyMax = sparklineData.length > 0 ? Math.max(...sparklineData) : null;
          const sparkStroke =
            resolvedValue === null
              ? (muiTheme: Theme): string => resolveCloneMetricAccentColor(muiTheme, 'red')
              : valueColor;
          const resolveTooltipSwatchColor = (
            muiTheme: Theme,
            sparkValue: number | null,
          ): string => {
            if (resolvedValue === null) {
              return resolveCloneMetricAccentColor(muiTheme, 'red');
            }
            const sparkAccent = getThresholdChipKey(sparkValue, thresholdGroup.frequency);
            const sparkTone = mapChipKeyToStatusTone(sparkAccent);
            return resolveCloneMetricAccentColor(muiTheme, sparkTone);
          };
          return (
            <CloneMetricSparkCell
              valueLabel={valueLabel}
              hasValue={resolvedValue !== null}
              deltaLabel={deltaLabel}
              deltaIsGood={deltaIsGood}
              deltaColor={deltaColor}
              yAxisMin={0}
              yAxisMax={frequencyMax ?? undefined}
              sparklineData={sparklineData}
              sparklineDates={sparklineDates}
              sparkStroke={sparkStroke}
              sparklineValueFormatter={(sparkValue) =>
                sparkValue === null ? '-' : Math.max(0, Math.round(sparkValue)).toLocaleString()
              }
              resolveTooltipSwatchColor={resolveTooltipSwatchColor}
            />
          );
        },
      }
    : null;

  const avgDurationSparklineColumn: GridColDef<Row> | null = avgDurationAsSparklineCell
    ? {
        field: 'metrics.averageDurationMs',
        headerName: 'Average Duration',
        flex: columnFlex.avgDuration,
        align: 'left',
        headerAlign: 'left',
        cellClassName: cellClassNames.avgDuration,
        filterable: false,
        sortable: true,
        valueGetter: (_value, row) => row.metrics?.averageDurationMs ?? null,
        renderCell: (params: GridRenderCellParams<Row>) => {
          const value = params.row.metrics?.averageDurationMs ?? null;
          const delta = showDeltas ? (params.row.metrics?.delta?.averageDurationMs ?? null) : null;
          const metricSeries = metricSeriesById?.get(params.row.id);
          const seriesIndex = resolveLatestSeriesIndex(metricSeries);
          const seriesValue = resolveSeriesValue(metricSeries?.averageDurationMs, seriesIndex);
          const seriesDelta = showDeltas
            ? resolveSeriesDelta(metricSeries?.averageDurationMs, seriesIndex)
            : null;
          const hasSeriesExecutions = seriesIndex !== null;
          const resolvedValue =
            metricSeries && !hasSeriesExecutions
              ? null
              : value !== null && value > 0
                ? value
                : (seriesValue ?? value);
          const resolvedDelta = showDeltas
            ? resolvedValue === null
              ? null
              : (delta ?? seriesDelta)
            : null;
          const valueLabel = resolvedValue === null ? 'No data' : formatDuration(resolvedValue);
          const deltaLabel =
            resolvedDelta === null
              ? null
              : resolvedDelta === 0
                ? 'No change'
                : `${resolvedDelta > 0 ? '+' : '-'}${formatDuration(Math.abs(resolvedDelta))}`;
          const thresholdGroup = resolvedThresholds(params.row);
          const accent = getThresholdChipKey(resolvedValue, thresholdGroup.avgDuration);
          const tone = mapChipKeyToStatusTone(accent);
          const valueColor =
            resolvedValue === null
              ? 'text.secondary'
              : (muiTheme: Theme): string => resolveCloneMetricAccentColor(muiTheme, tone);
          const deltaIsGood = resolvedDelta === null ? null : resolvedDelta < 0;
          const deltaColor = 'text.secondary';
          const sparklineData = metricSeries?.averageDurationMs ?? [];
          const sparklineDates = metricSeries?.dates ?? [];
          const sparkStroke = valueColor;
          const resolveTooltipSwatchColor = (
            muiTheme: Theme,
            sparkValue: number | null,
          ): string => {
            const sparkAccent = getThresholdChipKey(sparkValue, thresholdGroup.avgDuration);
            const sparkTone = mapChipKeyToStatusTone(sparkAccent);
            return resolveCloneMetricAccentColor(muiTheme, sparkTone);
          };
          return (
            <CloneMetricSparkCell
              valueLabel={valueLabel}
              hasValue={resolvedValue !== null}
              deltaLabel={deltaLabel}
              deltaIsGood={deltaIsGood}
              deltaColor={deltaColor}
              deltaArrowMode="duration"
              yAxisMin={0}
              sparklineData={sparklineData}
              sparklineDates={sparklineDates}
              sparkStroke={sparkStroke}
              sparklineValueFormatter={(sparkValue) =>
                sparkValue === null ? '-' : formatDuration(sparkValue)
              }
              resolveTooltipSwatchColor={resolveTooltipSwatchColor}
            />
          );
        },
      }
    : null;

  return { passRateSparklineColumn, frequencySparklineColumn, avgDurationSparklineColumn };
};
